"use client";

import { Clock, FileText, MessageCircle, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Symmetry() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    icon: Clock,
    title: "Concierge scheduling, on your terms",
    description: "Skip the waiting rooms and traffic. We bring professional aesthetic care directly to your preferred environment."
  }, {
    icon: MessageCircle,
    title: "Direct access to your injector",
    description: "Communicate directly with Dr. Ojeda, FNP for questions, follow-ups, or guidance. We prioritize your peace of mind."
  }, {
    icon: FileText,
    title: "Holistic facial balancing",
    description: "We focus on subtle, refined proportions that enhance your natural features rather than masking them."
  }, {
    icon: ShieldCheck,
    title: "Clinical-grade safety and privacy",
    description: "Your health data and aesthetic journey are handled with the highest standards of clinical confidentiality."
  }];
  return <section data-section-id="19003" id="symmetry-proportion" className="bg-background text-foreground border-t border-border/40 py-24 md:py-32 light">
      <div className="mx-auto max-w-[1240px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={imgRef} className={`lg:col-span-6 transition-all duration-700 ease-out ${imgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
            <div className="overflow-hidden rounded-lg shadow-md bg-card">
              <div className="relative aspect-[5/6] w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/facial-balancing-mapping-consultation.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/facial-balancing-mapping-consultation.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/facial-balancing-mapping-consultation.webp-wvc-sizes" alt="Dr. Amber Ojeda performing a personalized facial assessment in a serene, private setting" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            </div>
          </div>

          {}
          <div ref={headRef} className={`lg:col-span-6 transition-all duration-700 ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary font-default">
              The Experience
            </p>

            <h2 className="mt-5 font-default text-foreground font-medium leading-[1.2] tracking-tight text-[clamp(2rem,4vw,3.25rem)]">
              Refined facial balancing meets{" "}
              <span className="relative inline-block italic font-serif">tailored care<svg aria-hidden="true" viewBox="0 0 220 14" preserveAspectRatio="none" className="absolute left-0 -bottom-2 h-3 w-full overflow-visible transition-opacity duration-500 opacity-100" data-ast-id="f:/src/pages/Philosophy/sections/Symmetry.tsx|sig:mon229" data-ast-start="3436" data-ast-end="4222" style={{
                transitionDelay: '200ms'
              }}><path d="M2 9 C 55 2, 165 2, 218 8" fill="none" stroke="hsl(15 45% 75%)" data-ast-id="f:/src/pages/Philosophy/sections/Symmetry.tsx|sig:9eany6" data-ast-start="3798" data-ast-end="4216" style={{
                  strokeDasharray: '240',
                  strokeDashoffset: '0',
                  transition: 'stroke-dashoffset 600ms ease-out 300ms'
                }} stroke-width="1.75" stroke-linecap="round"></path></svg></span>
            </h2>

            <p className="mt-8 text-lg md:text-xl text-muted-foreground font-default leading-[1.7] max-w-[480px]">We believe neurotoxins and fillers are tools for restoration and symmetry. Dr. Ojeda, FNP combines clinical precision with an artistic eye to ensure your results are always natural, never overdone.</p>

            <ul className="mt-10 flex flex-col">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <li key={i} data-index={i} className={`group flex items-start gap-5 py-6 ${i !== features.length - 1 ? "border-b border-border/60" : ""}`}>
                    <div className="mt-1 size-10 flex items-center justify-center rounded-lg bg-primary/5">
                      <Icon className="size-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-foreground font-default font-medium text-[1.125rem] leading-snug tracking-tight">
                        {feature.title}
                      </h3>
                      <p className="mt-1.5 text-muted-foreground font-default leading-[1.7] text-[1rem]">
                        {feature.description}
                      </p>
                    </div>
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}